// The transaction list grid: tick-box column, sortable sticky header, rows
// loaded 50 at a time, per-row actions and a totals row that covers every
// matching record (not only the loaded ones).
import React, { useEffect, useMemo, useRef } from "react";
import { Edit2, Eye, Trash2 } from "feather-icons-react/build/IconComponents";
import { dmy, hueOf, initialsOf, intg, money, num } from "../../report/reportFormat";
import { agoOf } from "./listFormat";

export const BATCH = 50;

export const isNumCol = (c) => c.type === "money" || c.type === "int";

const dash = (v) => (v == null || v === "" ? "—" : v);

const Cell = ({ row, c }) => {
  if (c.cell) return <td className={c.className || ""}>{c.cell(row)}</td>;
  const v = row[c.k];
  switch (c.type) {
    case "date":
      return (
        <td className="axt-date">
          {v ? dmy(v) : "—"}
          {c.ago && v && <span className="axt-ago">{agoOf(v)}</span>}
        </td>
      );
    case "doc": {
      const b = c.badge ? c.badge(row) : null;
      return (
        <td>
          <div className="axt-doc">
            <span className="axt-docno">{dash(v)}</span>
            {b && b.text && <span className={"axt-src " + (b.tone || "")}>{b.text}</span>}
          </div>
        </td>
      );
    }
    case "party": {
      const name = dash(v);
      const sub = c.sub ? c.sub(row) : "";
      return (
        <td>
          <div className="axt-party">
            <span className="axr-av" style={{ "--h": hueOf(name) }}>
              {initialsOf(name)}
            </span>
            <span className="axt-pt">
              <b>{name}</b>
              {sub ? <span>{sub}</span> : null}
            </span>
          </div>
        </td>
      );
    }
    case "chip": {
      const p = c.chip ? c.chip(row) : { text: v, tone: "" };
      return (
        <td>
          {p && p.text ? (
            <span className={"axt-chip " + (p.tone || "")}>
              <i />
              {p.text}
            </span>
          ) : (
            <span className="axt-muted">—</span>
          )}
        </td>
      );
    }
    case "money":
      return c.k === "net" || c.hero ? (
        <td className="axt-amt">{money(v)}</td>
      ) : (
        <td className={"axt-num" + (num(v) === 0 ? " muted" : "")}>{money(v)}</td>
      );
    case "int":
      return <td className="axt-num">{intg(v)}</td>;
    default:
      return <td className={v == null || v === "" ? "axt-muted" : ""}>{dash(v)}</td>;
  }
};

const TransactionTable = ({
  columns,
  list,
  shown,
  onMore,
  sort,
  onSort,
  rowKey,
  selKey,
  onRowClick,
  picked,
  onPick,
  onPickAll,
  onEdit,
  onDelete,
  canEdit,
  editLabel = "Edit",
  canDelete,
  unit,
  emptyTitle,
  emptyHint,
  emptyAction,
}) => {
  const wrapRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
  }, [list]);

  useEffect(() => {
    const root = wrapRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onMore();
      },
      { root, rootMargin: "260px" }
    );
    io.observe(target);
    return () => io.disconnect();
    // re-observing after each batch re-checks a sentinel that is still in view
  }, [list, shown, onMore]);

  // keep the keyboard-selected row in view
  useEffect(() => {
    if (selKey === undefined || selKey === null || !wrapRef.current) return;
    const tr = Array.from(wrapRef.current.querySelectorAll("tr[data-key]")).find(
      (x) => x.dataset.key === String(selKey)
    );
    if (tr && tr.scrollIntoView) tr.scrollIntoView({ block: "nearest" });
  }, [selKey]);

  const totals = useMemo(() => {
    const acc = {};
    columns.forEach((c) => {
      if (isNumCol(c) && !c.noSum) acc[c.k] = list.reduce((a, r) => a + num(r[c.k]), 0);
    });
    return acc;
  }, [list, columns]);

  const allPicked = list.length > 0 && list.every((r) => picked.has(rowKey(r)));
  let labelled = false;

  return (
    <div className="axr-tablewrap axt-tablewrap" ref={wrapRef}>
      <table className="axr-rpt axt-list">
        <thead>
          <tr>
            <th className="axt-chk">
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allPicked}
                onChange={(e) => onPickAll(e.target.checked)}
              />
            </th>
            {columns.map((c) => (
              <th key={c.k} className={isNumCol(c) ? "r" : ""} style={c.w ? { width: c.w } : undefined}>
                <button type="button" onClick={() => onSort(c)}>
                  {c.t}
                  {sort.k === (c.sortKey || c.k) && <span className="axr-ar">{sort.d > 0 ? "▲" : "▼"}</span>}
                </button>
              </th>
            ))}
            <th className="axt-actcol" />
          </tr>
        </thead>
        <tbody>
          {list.slice(0, shown).map((r) => {
            const key = rowKey(r);
            const isPicked = picked.has(key);
            return (
              <tr
                key={key}
                data-key={key}
                tabIndex={0}
                className={(key === selKey ? "sel " : "") + (isPicked ? "picked" : "")}
                onClick={(e) => {
                  if (e.target.closest(".axt-chk") || e.target.closest(".axr-rowacts")) return;
                  onRowClick(r);
                }}
              >
                <td className="axt-chk">
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.billNo || ""}`}
                    checked={isPicked}
                    onChange={(e) => onPick(key, e.target.checked)}
                  />
                </td>
                {columns.map((c) => (
                  <Cell key={c.k} row={r} c={c} />
                ))}
                <td>
                  <div className="axr-rowacts">
                    <button
                      type="button"
                      className="axr-rowbtn"
                      title="Preview"
                      aria-label="Preview"
                      onClick={() => onRowClick(r)}
                    >
                      <Eye />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="axr-rowbtn"
                        title={editLabel}
                        aria-label={editLabel}
                        onClick={() => onEdit(r)}
                      >
                        <Edit2 />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="axr-rowbtn del"
                        title="Delete"
                        aria-label="Delete"
                        onClick={() => onDelete(r)}
                      >
                        <Trash2 />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {list.length > 0 && (
          <tfoot>
            <tr>
              <td />
              {columns.map((c) => {
                if (isNumCol(c) && !c.noSum) {
                  return (
                    <td key={c.k} className={"r" + (c.k === "net" || c.hero ? " axr-net" : "")}>
                      {c.type === "int" ? intg(totals[c.k]) : money(totals[c.k])}
                    </td>
                  );
                }
                if (!labelled) {
                  labelled = true;
                  return (
                    <td key={c.k} className="axr-flbl">
                      Total · {intg(list.length)} {unit}
                    </td>
                  );
                }
                return <td key={c.k} />;
              })}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
      <div className="axr-sentinel" ref={sentinelRef} />
      {list.length === 0 && (
        <div className="axr-empty axt-empty">
          <svg viewBox="0 0 48 48" width="38" height="38" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M11 5h20l6 6v32H11z" />
            <path d="M31 5v6h6M17 20h14M17 27h14M17 34h8" />
          </svg>
          <div className="axr-big">{emptyTitle}</div>
          <div>{emptyHint}</div>
          {emptyAction && <div className="axt-emptyact">{emptyAction}</div>}
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
